import { Disclosure } from '@headlessui/react';
import classNames from 'classnames';

import { Card } from 'shared/ui/card/card';
import { ScoreSquare } from 'shared/ui/score-square/score-square';
import { Chip } from 'shared/ui/chip/chip';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { useTranslation } from 'hooks';
import Button from 'shared/ui/button/button';
import { linkifyText } from 'shared/helpers/linkifyText';

import styles from './rule-card.module.scss';

export interface RuleCardProps {
  variant: 'technical' | 'product' | 'analysis';
  title: string;
  description?: string;
  buttonText?: string;
  onOpen?: () => void;
  restricted?: boolean;
  isTrialExpired?: boolean;
  score?: 1 | 2 | 3;
  analysisScore?: number;
  maxScore?: number;
  showScore?: boolean;
  showChip?: boolean;
  chipContent?: React.ReactNode;
  color?: 'gray' | 'white';
  chipColor?: 'green' | 'gray' | 'orange' | 'red' | 'blue';
}

export const RuleCard = ({
  variant,
  title,
  description,
  buttonText,
  onOpen,
  restricted = false,
  isTrialExpired = false,
  score,
  analysisScore,
  maxScore,
  showScore = true,
  showChip = true,
  chipContent,
  color = 'gray',
  chipColor = 'gray',
}: RuleCardProps) => {
  const { t } = useTranslation();
  const isLocked = restricted || isTrialExpired;

  const scoreTitle = score
    ? ({ 1: t('bad'), 2: t('average'), 3: t('good') } as const)[score]
    : '';

  const scoreColor = score
    ? ({ 1: 'red', 2: 'orange', 3: 'green' } as const)[score]
    : 'gray';

  if (variant === 'analysis') {
    return (
      <Disclosure>
        <Card className={styles.analysisCard}>
          <div>
            <h3>{t(title)}</h3>
            <p>{linkifyText(t(description ?? ''))}</p>
            <Button
              theme={'tonal'}
              tone={'default'}
              onClick={onOpen}
              disabled={isLocked}
            >
              {isLocked ? (
                <span className={styles.lockedButtonContent}>
                  <Icon pathToIcon={ICONS.lockGray} size={16} />
                  {t('availableInFullAnalysis')}
                </span>
              ) : (
                (buttonText ?? t('openAnalysis'))
              )}
            </Button>
          </div>
          {chipContent ? (
            <div className={styles.analysisChip}>
              <Chip color={chipColor}>
                <span>{chipContent}</span>
              </Chip>
            </div>
          ) : typeof analysisScore === 'number' ? (
            <ScoreSquare
              score={analysisScore}
              maxScore={maxScore}
              isLocked={isTrialExpired}
            />
          ) : null}
        </Card>
      </Disclosure>
    );
  }

  if (variant === 'technical') {
    const renderTechnicalStatusBadge = () => {
      if (isTrialExpired || restricted) {
        return (
          <div className={styles.lockBadge}>
            <Icon pathToIcon={ICONS.lockGray} size={14} />
          </div>
        );
      }
      // TODO: Uncomment when backend API is ready and returns real status
      // return (
      //   <Chip color={status === 'pass' ? 'green' : 'red'}>
      //     <span>{status === 'pass' ? t('Pass') : t('NeedFix')}</span>
      //   </Chip>
      // );
      return (
        <Chip color='gray'>
          <span>{t('analysisBeAvailableSoon')}</span>
        </Chip>
      );
    };

    return (
      <Disclosure>
        <div className={classNames(styles.ruleCard, styles.withBorder)}>
          <div className={styles.header}>
            <span
              className={
                isTrialExpired || restricted ? styles.titleTrial : styles.title
              }
            >
              {t(title)}
            </span>
            {renderTechnicalStatusBadge()}
          </div>

          {isTrialExpired || restricted ? (
            <div className={styles.trialChip}>
              <Icon pathToIcon={ICONS.lockGray} size={12} />
              <span>{t('availableInFullAnalysis')}</span>
            </div>
          ) : (
            <>
              {description && (
                <p className={styles.description}>{linkifyText(description)}</p>
              )}
              <div className={styles.footer}>
                <Button
                  theme='tonal'
                  tone='default'
                  className={styles.buttonHeight32}
                  onClick={onOpen}
                  disabled={restricted}
                >
                  {restricted ? (
                    <>
                      <Icon pathToIcon={ICONS.lockGray} size={16} />
                      {buttonText ?? t('openDetails')}
                    </>
                  ) : (
                    (buttonText ?? t('openDetails'))
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Disclosure>
    );
  }

  const renderProductStatusBadge = () => {
    if (isTrialExpired) {
      return (
        <div className={styles.lockBadge}>
          <Icon pathToIcon={ICONS.lockGray} size={14} />
        </div>
      );
    }
    return (
      <Chip color={showChip ? scoreColor : 'gray'}>
        {showChip ? (
          showScore ? (
            <span>
              {t('websiteScore')} <b>• {scoreTitle}</b>
            </span>
          ) : (
            <span>{score === 3 ? t('Pass') : t('NeedFix')}</span>
          )
        ) : (
          <span>{t('analysisBeAvailableSoon')}</span>
        )}
      </Chip>
    );
  };

  return (
    <Disclosure>
      <div className={classNames(styles.ruleCard, styles[color])}>
        <div className={styles.header}>
          <span className={isTrialExpired ? styles.titleTrial : styles.title}>
            {t(title)}
          </span>
          {renderProductStatusBadge()}
        </div>

        {isTrialExpired ? (
          <div className={styles.trialChip}>
            <Icon pathToIcon={ICONS.lockGray} size={12} />
            <span>{t('availableInFullAnalysis')}</span>
          </div>
        ) : (
          <>
            {description && (
              <p className={styles.description}>{linkifyText(description)}</p>
            )}

            <div className={styles.footer}>
              <Button
                theme='tonal'
                tone='default'
                className={styles.buttonHeight32}
                onClick={onOpen}
                disabled={restricted}
              >
                {restricted ? (
                  <span className={styles.lockedButtonContent}>
                    <Icon pathToIcon={ICONS.lockGray} size={16} />
                    {t('availableInFullAnalysis')}
                  </span>
                ) : (
                  (buttonText ?? t('openAnalysis'))
                )}
              </Button>

              {showChip && !restricted && (
                <Chip color='gray'>
                  {chipContent ?? <span>0 {t('casesFromWebsite')}</span>}
                </Chip>
              )}
            </div>
          </>
        )}
      </div>
    </Disclosure>
  );
};

export default RuleCard;
